import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
	type AnyCol,
	PdfExportDialog,
	type PdfExportDialogState,
	type PdfMeta,
	buildPdfShowAllInfo,
	exportArchiveGridToExcel,
	headerOf,
} from "@/shared/data-grid";
import FilterAltRoundedIcon from "@mui/icons-material/FilterAltRounded";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import TableChartIcon from "@mui/icons-material/TableChart";
import TableViewOutlinedIcon from "@mui/icons-material/TableViewOutlined";
import ViewWeekRoundedIcon from "@mui/icons-material/ViewWeekRounded";
import { ListItemIcon, ListItemText, MenuItem } from "@mui/material";
import {
	type GridApiCommunity,
	type GridColDef,
	GridPreferencePanelsValue,
	useGridApiRef,
} from "@mui/x-data-grid";

type UseTaskTableHeaderActionsArgs = {
	setTaskHeaderActions: (node: React.ReactNode) => void;
	isMobile: boolean;
	rows: Record<string, unknown>[];
	columns: GridColDef[];
	fileName: string;
	pdfMeta?: PdfMeta;
	leadingActions?: React.ReactNode;
	enableGridTools?: boolean;
};

const taskMenuItemSx = {
	mx: 0.75,
	my: 0.25,
	borderRadius: 2,
	px: 1,
	py: 0.75,
	minHeight: 38,
	"& .MuiListItemIcon-root": {
		minWidth: 30,
		color: "text.primary",
	},
	"& .MuiListItemText-primary": {
		fontSize: 14.5,
		fontWeight: 500,
		letterSpacing: 0.1,
	},
	"&:hover": { backgroundColor: "action.hover" },
};

export function useTaskTableHeaderActions({
	setTaskHeaderActions,
	isMobile,
	rows,
	columns,
	fileName,
	pdfMeta,
	leadingActions,
	enableGridTools = true,
}: UseTaskTableHeaderActionsArgs) {
	const apiRef = useGridApiRef<GridApiCommunity>();
	const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
	const [isPdfExporting, setIsPdfExporting] = useState(false);
	const [selectedPdfFields, setSelectedPdfFields] = useState<Set<string>>(
		() => new Set(),
	);
	const [pdfExportOpts, setPdfExportOpts] = useState<PdfExportDialogState>({
		dateFormat: pdfMeta?.dateFormat ?? "DD/MM/YY HH:mm:ss",
		footerText: pdfMeta?.footerText ?? "Satos",
		truncateAt: pdfMeta?.truncateAt ?? 200,
		truncateEnabled: false,
		pageSize: pdfMeta?.pageSize ?? "a4",
		groupBy: "",
	});

	const getVisibleColumns = useCallback((): AnyCol[] => {
		const visible = apiRef.current?.getVisibleColumns?.();
		if (Array.isArray(visible) && visible.length > 0) {
			return visible.map((col) => ({
				field: col.field,
				headerName: col.headerName,
				header: col.headerName,
				hide: false,
				minWidth: col.minWidth,
			}));
		}
		return columns
			.filter((col) => col.hide !== true)
			.map((col) => ({
				field: col.field,
				headerName: col.headerName,
				header: col.headerName,
				hide: false,
				minWidth: col.minWidth,
			}));
	}, [apiRef, columns]);

	const handleOpenColumnsPanel = useCallback(() => {
		if (isMobile || !enableGridTools) return;

		const api = apiRef.current;
		if (!api) return;

		const preferencePanel = api.state.preferencePanel;
		if (
			preferencePanel?.open &&
			preferencePanel?.openedPanelValue === GridPreferencePanelsValue.columns
		) {
			api.hidePreferences?.();
			return;
		}

		api.showPreferences?.(GridPreferencePanelsValue.columns);
	}, [apiRef, enableGridTools, isMobile]);

	const handleOpenFilterPanel = useCallback(() => {
		if (isMobile || !enableGridTools) return;

		const api = apiRef.current;
		if (!api) return;

		const preferencePanel = api.state.preferencePanel;
		if (
			preferencePanel?.open &&
			preferencePanel?.openedPanelValue === GridPreferencePanelsValue.filters
		) {
			api.hidePreferences?.();
			return;
		}

		api.showPreferences?.(GridPreferencePanelsValue.filters);
	}, [apiRef, enableGridTools, isMobile]);

	const handleExportCsv = useCallback(() => {
		if (!enableGridTools) return;
		apiRef.current?.exportDataAsCsv?.({
			fileName,
			utf8WithBom: true,
			delimiter: ",",
			includeHeaders: true,
		});
	}, [apiRef, enableGridTools, fileName]);

	const handleExportExcel = useCallback(() => {
		if (!enableGridTools) return;
		void exportArchiveGridToExcel(apiRef, { fileName });
	}, [apiRef, enableGridTools, fileName]);

	const openPdfExportDialog = useCallback(() => {
		const visibleColumns = getVisibleColumns();
		setSelectedPdfFields(new Set(visibleColumns.map((col) => col.field)));
		setPdfExportOpts((prev) => ({
			...prev,
			dateFormat: pdfMeta?.dateFormat ?? prev.dateFormat,
			footerText: pdfMeta?.footerText ?? prev.footerText,
			truncateAt: pdfMeta?.truncateAt ?? prev.truncateAt,
			pageSize: pdfMeta?.pageSize ?? prev.pageSize,
		}));
		setPdfDialogOpen(true);
	}, [getVisibleColumns, pdfMeta]);

	const togglePdfColumn = useCallback((field: string) => {
		setSelectedPdfFields((prev) => {
			const next = new Set(prev);
			if (next.has(field)) next.delete(field);
			else next.add(field);
			return next;
		});
	}, []);

	const selectAllPdfColumns = useCallback(() => {
		setSelectedPdfFields(new Set(getVisibleColumns().map((col) => col.field)));
	}, [getVisibleColumns]);

	const deselectAllPdfColumns = useCallback(() => {
		setSelectedPdfFields(new Set());
	}, []);

	const handlePdfExport = useCallback(async () => {
		const visibleColumns = getVisibleColumns();
		const selectedCols = visibleColumns.filter((col) =>
			selectedPdfFields.has(col.field),
		);
		if (selectedCols.length === 0) return;

		setIsPdfExporting(true);
		try {
			await buildPdfShowAllInfo(rows, visibleColumns, fileName, pdfMeta, {
				selectedColumns: selectedCols,
				dateFormat: pdfExportOpts.dateFormat,
				footerText: pdfExportOpts.footerText || "Satos",
				truncateAt: pdfExportOpts.truncateEnabled
					? pdfExportOpts.truncateAt || 200
					: undefined,
				pageSize: pdfExportOpts.pageSize,
				groupBy: pdfExportOpts.groupBy || undefined,
			});
			setPdfDialogOpen(false);
		} catch (e) {
			console.error("PDF export failed:", e);
		} finally {
			setIsPdfExporting(false);
		}
	}, [
		fileName,
		getVisibleColumns,
		pdfExportOpts,
		pdfMeta,
		rows,
		selectedPdfFields,
	]);

	useEffect(() => {
		const hasAnyActions = Boolean(leadingActions) || enableGridTools;
		if (!hasAnyActions) {
			setTaskHeaderActions(null);
			return () => setTaskHeaderActions(null);
		}

		const actions = (
			<>
				{leadingActions}
				{enableGridTools && !isMobile && (
					<MenuItem sx={taskMenuItemSx} onClick={handleOpenColumnsPanel}>
						<ListItemIcon>
							<ViewWeekRoundedIcon fontSize="small" />
						</ListItemIcon>
						<ListItemText primary="Columns" />
					</MenuItem>
				)}
				{enableGridTools && !isMobile && (
					<MenuItem sx={taskMenuItemSx} onClick={handleOpenFilterPanel}>
						<ListItemIcon>
							<FilterAltRoundedIcon fontSize="small" />
						</ListItemIcon>
						<ListItemText primary="Filters" />
					</MenuItem>
				)}
				{enableGridTools && (
					<MenuItem
						sx={taskMenuItemSx}
						onClick={handleExportCsv}
						disabled={isMobile || rows.length === 0}
					>
						<ListItemIcon>
							<TableViewOutlinedIcon fontSize="small" />
						</ListItemIcon>
						<ListItemText primary="Export CSV" />
					</MenuItem>
				)}
				{enableGridTools && (
					<MenuItem
						sx={taskMenuItemSx}
						onClick={handleExportExcel}
						disabled={isMobile || rows.length === 0}
					>
						<ListItemIcon>
							<TableChartIcon fontSize="small" />
						</ListItemIcon>
						<ListItemText primary="Export Excel" />
					</MenuItem>
				)}
				{enableGridTools && (
					<MenuItem
						sx={taskMenuItemSx}
						onClick={openPdfExportDialog}
						disabled={rows.length === 0}
					>
						<ListItemIcon>
							<PictureAsPdfOutlinedIcon fontSize="small" />
						</ListItemIcon>
						<ListItemText primary="Export PDF" />
					</MenuItem>
				)}
			</>
		);

		setTaskHeaderActions(actions);
		return () => setTaskHeaderActions(null);
	}, [
		enableGridTools,
		handleExportCsv,
		handleExportExcel,
		handleOpenColumnsPanel,
		handleOpenFilterPanel,
		isMobile,
		leadingActions,
		openPdfExportDialog,
		rows.length,
		setTaskHeaderActions,
	]);

	const pdfDialog = enableGridTools ? (
		<PdfExportDialog
			open={pdfDialogOpen}
			onClose={() => !isPdfExporting && setPdfDialogOpen(false)}
			isExporting={isPdfExporting}
			cols={getVisibleColumns()}
			headerOf={headerOf}
			selectedFields={selectedPdfFields}
			toggleColumn={togglePdfColumn}
			selectAllColumns={selectAllPdfColumns}
			deselectAllColumns={deselectAllPdfColumns}
			exportOpts={pdfExportOpts}
			setExportOpts={setPdfExportOpts}
			onExport={() => {
				void handlePdfExport();
			}}
			canExport={selectedPdfFields.size > 0}
		/>
	) : null;

	const toolbarProps = useMemo(
		() => ({
			hidden: true,
			rows,
			columns,
			fileName,
			hideQuickFilter: true,
			showPdfOptions: true,
			pdfMeta,
		}),
		[columns, fileName, pdfMeta, rows],
	);

	return {
		apiRef,
		pdfDialog,
		toolbarProps,
	};
}
